import React, { useState, useEffect } from 'react';
import { X, Info } from 'lucide-react';

interface InitialData {
    id: string;
    provider?: string;
    endpointUrl?: string;
    apiKey?: string;
    collectionName?: string;
    description?: string;
    isActive?: boolean;
    additionalConfigJson?: string;
}

interface Props {
    onClose: () => void;
    onSubmit: (data: any) => void | Promise<void>;
    initialData?: InitialData | null;
}

type ProviderId = 'Pinecone' | 'Qdrant' | 'ChromaDB' | 'Weaviate' | 'Milvus' | 'FAISS' | '';

interface ProviderField {
    id: string;
    label: string;
    placeholder: string;
    required: boolean;
    type?: 'text' | 'password' | 'url';
    help?: string;
}

interface ProviderConfig {
    id: ProviderId;
    label: string;
    backendValue: string; // API enum: Pinecone | Qdrant | Chroma | Milvus | Custom
    hint: string;
    fields: ProviderField[];
}

const PROVIDER_CONFIGS: ProviderConfig[] = [
    {
        id: 'Pinecone',
        label: 'Pinecone',
        backendValue: 'Pinecone',
        hint: 'Cloud-hosted vector DB. Use your Pinecone API key and optionally the index name. Serverless uses API key; specify environment only if using legacy host.',
        fields: [
            { id: 'apiKey', label: 'API Key', placeholder: 'Pinecone API key from console.pinecone.io', required: true, type: 'password', help: 'Required. Find it in Pinecone dashboard under API Keys.' },
            { id: 'environment', label: 'Environment (optional)', placeholder: 'e.g. us-east-1-aws (legacy host only)', required: false, type: 'text', help: 'Only for legacy Pinecone; leave blank for serverless.' },
        ],
    },
    {
        id: 'Qdrant',
        label: 'Qdrant',
        backendValue: 'Qdrant',
        hint: 'Self-hosted or Qdrant Cloud. Provide the REST API URL and index name.',
        fields: [
            { id: 'endpoint', label: 'Endpoint URL', placeholder: 'e.g. http://localhost:6333 or https://xxx.qdrant.io', required: true, type: 'url', help: 'Qdrant REST API base URL (port 6333 for local).' },
            { id: 'apiKey', label: 'API Key (optional)', placeholder: 'For Qdrant Cloud authentication', required: false, type: 'password', help: 'Required only for Qdrant Cloud.' },
        ],
    },
    {
        id: 'ChromaDB',
        label: 'Chroma',
        backendValue: 'Chroma',
        hint: 'Open-source embedding store. Default is localhost; set endpoint if using a remote Chroma server.',
        fields: [
            { id: 'endpoint', label: 'Endpoint URL (optional)', placeholder: 'e.g. http://localhost:8000', required: false, type: 'url', help: 'Leave blank for default http://localhost:8000.' },
            { id: 'apiKey', label: 'Auth token (optional)', placeholder: 'If Chroma server uses authentication', required: false, type: 'password' },
        ],
    },
    {
        id: 'Weaviate',
        label: 'Weaviate',
        backendValue: 'Custom',
        hint: 'GraphQL-based vector DB. Provide Weaviate instance URL and optional API key for Weaviate Cloud.',
        fields: [
            { id: 'endpoint', label: 'Weaviate URL', placeholder: 'e.g. https://your-cluster.weaviate.network', required: true, type: 'url', help: 'Weaviate instance base URL.' },
            { id: 'apiKey', label: 'API Key (optional)', placeholder: 'For Weaviate Cloud', required: false, type: 'password' },
        ],
    },
    {
        id: 'Milvus',
        label: 'Milvus',
        backendValue: 'Milvus',
        hint: 'Distributed vector DB. Provide URI (host:port) or HTTP endpoint and optional credentials.',
        fields: [
            { id: 'endpoint', label: 'Endpoint / URI', placeholder: 'e.g. http://localhost:19530 or https://xxx.zillizcloud.com', required: true, type: 'url', help: 'Milvus gRPC (19530) or HTTP endpoint.' },
            { id: 'apiKey', label: 'Username / API Key (optional)', placeholder: 'For Milvus Cloud or secured cluster', required: false, type: 'password' },
        ],
    },
    {
        id: 'FAISS',
        label: 'FAISS',
        backendValue: 'Custom',
        hint: 'Local or file-based index. No remote endpoint; use a local path or leave endpoint blank for in-memory.',
        fields: [
            { id: 'endpoint', label: 'Index path (optional)', placeholder: 'e.g. file:///path/to/faiss/index', required: false, type: 'text', help: 'File path or URI for persisted FAISS index.' },
        ],
    },
];

const vectorProviders = PROVIDER_CONFIGS;

const providerToFrontend: Record<string, ProviderId> = {
    Pinecone: 'Pinecone',
    Qdrant: 'Qdrant',
    Chroma: 'ChromaDB',
    Milvus: 'Milvus',
    Custom: 'FAISS',
};

const AddVectorStoreModal: React.FC<Props> = ({ onClose, onSubmit, initialData }) => {
    const [name, setName] = useState('');
    const [type, setType] = useState<ProviderId>('');
    const [tenantClient, setTenantClient] = useState('');
    const [intendedUsage, setIntendedUsage] = useState('');
    const [region, setRegion] = useState('');
    const [endpoint, setEndpoint] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [environment, setEnvironment] = useState('');
    const [status, setStatus] = useState(true);
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const isEdit = Boolean(initialData?.id);

    useEffect(() => {
        if (!initialData) return;
        setName(initialData.collectionName ?? initialData.description ?? '');
        setType(providerToFrontend[initialData.provider ?? ''] ?? 'FAISS');
        setEndpoint(initialData.endpointUrl ?? '');
        setApiKey(initialData.apiKey ?? '');
        setStatus(initialData.isActive ?? true);
        setDescription(initialData.description ?? '');
        try {
            const extra = initialData.additionalConfigJson ? JSON.parse(initialData.additionalConfigJson) : {};
            setRegion(extra.Region ?? '');
            setIntendedUsage(extra.IntendedUsage ?? '');
            setTenantClient(extra.TenantOrClient ?? '');
        } catch {
            /* ignore */
        }
    }, [initialData]);

    const selectedConfig = type ? PROVIDER_CONFIGS.find((c) => c.id === type) : null;

    const getFieldValue = (fieldId: string): string => {
        switch (fieldId) {
            case 'endpoint': return endpoint;
            case 'apiKey': return apiKey;
            case 'environment': return environment;
            default: return '';
        }
    };
    const setFieldValue = (fieldId: string, value: string) => {
        switch (fieldId) {
            case 'endpoint': setEndpoint(value); break;
            case 'apiKey': setApiKey(value); break;
            case 'environment': setEnvironment(value); break;
            default: break;
        }
    };

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    const handleSubmit = async (e?: React.MouseEvent) => {
        e?.preventDefault?.();
        if (!name?.trim() || !type) return;
        const config = PROVIDER_CONFIGS.find((c) => c.id === type);
        const requiredMissing = config?.fields?.filter((f) => f.required && !getFieldValue(f.id).trim());
        if (requiredMissing?.length) {
            setSaveError(`Please fill required fields: ${requiredMissing.map((f) => f.label).join(', ')}`);
            return;
        }
        setSaveError(null);
        setSaving(true);
        const parts: string[] = [];
        if (description) parts.push(description);
        if (environment) parts.push('Environment: ' + environment);
        const desc = parts.length > 0 ? parts.join(' | ') : undefined;
        const payload = {
            id: isEdit ? initialData!.id : `ds_${Date.now()}`,
            name: name.trim(),
            type: config?.backendValue ?? type,
            tenantClient: tenantClient.trim() || undefined,
            intendedUsage: intendedUsage.trim() || undefined,
            region: region.trim() || undefined,
            endpoint,
            apiKey,
            environment: environment.trim() || undefined,
            createdOn: new Date().toISOString().split('T')[0],
            status: status ? 'Active' : 'Inactive',
            description: desc,
        };
        try {
            await onSubmit(payload);
            onClose();
        } catch (err: any) {
            setSaveError(err?.response?.data?.message || err?.message || 'Failed to save vector store.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4 overflow-y-auto">
            <div className="bg-neutral-900 w-full max-w-3xl rounded-xl shadow-lg border border-neutral-800 flex flex-col max-h-[90vh] my-auto">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-800 shrink-0">
                    <h2 className="text-white text-lg font-semibold">{isEdit ? 'Edit Vector Store' : 'Add New Vector Store'}</h2>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-white" disabled={saving}>
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form - all fields per Add New Vector Store UI */}
                <div className="p-6 space-y-5 text-sm text-white">
                    {saveError && <p className="text-red-400 text-sm">{saveError}</p>}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-gray-400 block mb-1">Store Name</label>
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                                placeholder="e.g. Customer Vectors"
                            />
                        </div>
                        <div>
                            <label className="text-gray-400 block mb-1">Provider Type</label>
                            <select
                                value={type}
                                onChange={(e) => {
                                    const next = (e.target.value || '') as ProviderId;
                                    setType(next);
                                    setEndpoint('');
                                    setApiKey('');
                                    setEnvironment('');
                                }}
                                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                            >
                                <option value="">Select Type</option>
                                <option value="">Select Type</option>
                                {vectorProviders.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-gray-400 block mb-1">Tenant / Client</label>
                            <input
                                value={tenantClient}
                                onChange={(e) => setTenantClient(e.target.value)}
                                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                                placeholder="e.g. Client A"
                            />
                        </div>
                        <div>
                            <label className="text-gray-400 block mb-1">Intended Usage</label>
                            <input
                                value={intendedUsage}
                                onChange={(e) => setIntendedUsage(e.target.value)}
                                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                                placeholder="e.g. Chat Memory, Retrieval"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-gray-400 block mb-1">Region / Location</label>
                            <input
                                value={region}
                                onChange={(e) => setRegion(e.target.value)}
                                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                                placeholder="e.g. us-east-1"
                            />
                        </div>
                        <div>
                            <label className="text-gray-400 block mb-1">API Endpoint (optional)</label>
                            <input
                                value={endpoint}
                                onChange={(e) => setEndpoint(e.target.value)}
                                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                                placeholder="e.g. https://..."
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-gray-400 block mb-1">API Key (optional)</label>
                        <input
                            type="password"
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                            placeholder="API key if required by provider"
                        />
                    </div>
                    <div>
                        <label className="text-gray-400 block mb-1">Description (optional)</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                            placeholder="Describe this datastore..."
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-gray-400 block mb-1">Tenant / Client</label>
                            <input
                                value={tenantClient}
                                onChange={(e) => setTenantClient(e.target.value)}
                                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                                placeholder="e.g. Client A"
                            />
                        </div>
                        <div>
                            <label className="text-gray-400 block mb-1">Intended Usage</label>
                            <input
                                value={intendedUsage}
                                onChange={(e) => setIntendedUsage(e.target.value)}
                                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                                placeholder="e.g. Chat Memory, Retrieval"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-gray-400 block mb-1">Region / Location</label>
                            <input
                                value={region}
                                onChange={(e) => setRegion(e.target.value)}
                                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                                placeholder="e.g. us-east-1"
                            />
                        </div>
                        <div>
                            <label className="text-gray-400 block mb-1">API Endpoint (optional)</label>
                            <input
                                value={endpoint}
                                onChange={(e) => setEndpoint(e.target.value)}
                                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                                placeholder="e.g. https://..."
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-gray-400 block mb-1">API Key (optional)</label>
                        <input
                            type="password"
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                            placeholder="API key if required by provider"
                        />
                    </div>
                    <div>
                        <label className="text-gray-400 block mb-1">Description (optional)</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                            placeholder="Describe this datastore..."
                        />
                    </div>

                    {selectedConfig && (
                        <div className="rounded-lg bg-neutral-800/60 border border-neutral-700 p-4 space-y-4">
                            <div className="flex gap-2 text-amber-200/90 text-xs">
                                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                                <p>{selectedConfig.hint}</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {selectedConfig.fields.map((field) => (
                                    <div key={field.id}>
                                        <label className="text-gray-400 block mb-1">
                                            {field.label}
                                            {field.required && <span className="text-red-400 ml-1">*</span>}
                                        </label>
                                        <input
                                            type={field.type === 'password' ? 'password' : 'text'}
                                            value={getFieldValue(field.id)}
                                            onChange={(e) => setFieldValue(field.id, e.target.value)}
                                            className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                                            placeholder={field.placeholder}
                                        />
                                        {field.help && (
                                            <p className="text-gray-500 text-xs mt-1">{field.help}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-2 mt-2">
                        <input
                            type="checkbox"
                            checked={status}
                            onChange={() => setStatus(!status)}
                            className="accent-indigo-500"
                        />
                        <label className="text-gray-300 text-sm">Mark as active</label>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center px-6 py-4 border-t border-neutral-800 bg-neutral-950 shrink-0">
                    {/* Left: Test Connection (placeholder for now) */}
                    <button
                        type="button"
                        disabled={saving}
                        className="bg-neutral-600 hover:bg-neutral-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm shadow cursor-not-allowed"
                        title="Coming soon"
                    >
                        Test Connection
                    </button>

                    {/* Right: Cancel / Save */}
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className="text-gray-400 hover:text-white text-sm disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={(e) => handleSubmit(e)}
                            disabled={!name?.trim() || !type || saving}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-md text-sm shadow disabled:opacity-40"
                        >
                            {saving ? 'Saving…' : isEdit ? 'Update Vector Store' : 'Save Vector Store'}
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AddVectorStoreModal;
